// BeanHub Shelf — the app's state. One module every screen shares.
//
// Hand-written, not generated: `appfactory product apply` would replace this
// file, so do not re-apply the plan without keeping it.
//
//   catalogue   the coffees and roasters (bundled snapshot, refreshed from /api/catalogue)
//   grouping    how the wall is racked: roaster | flavour | brew | origin
//   filters     what is lit on the wall; everything else is dimmed, never removed
//   bays        the wall's layout, derived from the two above
//   bag         the virtual shopping bag, persisted on this device
//   settings    view mode, motion, graphics quality

import { ref, computed, watch, reactive } from 'vue';
import snapshot from './data/catalogue.json';
import { prefs } from './core/storage.js';
import { isDemo } from './core/demo.js';
import { API_BASE } from './core/native.js';
import { prefersReducedMotion } from './core/a11y.js';

// --- catalogue ----------------------------------------------------------------

export const catalogue = ref(snapshot);
export const coffees = computed(() => catalogue.value.coffees);
export const roasters = computed(() => catalogue.value.roasters);
export const syncedAt = computed(() => new Date(catalogue.value.syncedAt));
export const catalogueStatus = ref('idle'); // idle | loading | fresh | offline

export const FLAVOURS = [
  { id: 'chocolate', name: 'Chocolate & Nutty', short: 'Chocolate', icon: 'nutrition', colour: '#6B3F2A' },
  { id: 'caramel', name: 'Caramel & Sweet', short: 'Caramel', icon: 'flask', colour: '#C98A2B' },
  { id: 'berry', name: 'Berry & Stone Fruit', short: 'Berry', icon: 'leaf', colour: '#A3324A' },
  { id: 'citrus', name: 'Citrus & Bright', short: 'Citrus', icon: 'sunny', colour: '#E0A800' },
  { id: 'tropical', name: 'Tropical & Funky', short: 'Tropical', icon: 'sparkles', colour: '#1E8C7E' },
  { id: 'floral', name: 'Floral & Tea-like', short: 'Floral', icon: 'flower', colour: '#7A5BA8' },
];
export const flavourById = Object.fromEntries(FLAVOURS.map((f) => [f.id, f]));
export const BREWS = ['Filter', 'Omni', 'Espresso'];
export const BREW_BLURB = {
  Filter: 'Roasted light for pour-over, batch brew and plunger.',
  Omni: 'Roasted to work as espresso or filter.',
  Espresso: 'Roasted for espresso machines and milk drinks.',
};
export const KINDS = ['Single Origin', 'Blend', 'Decaf'];

export const coffeeBySlug = (slug) => coffees.value.find((c) => c.slug === slug) || null;
export const roasterBySlug = (slug) => roasters.value.find((r) => r.slug === slug) || null;

// A roaster's bag colour, from the sync (their photos) or a stable fallback.
const FALLBACK = ['#C8955B', '#3E6B5A', '#8C3B2E', '#2F4A6D', '#B5651D', '#5D4E8C', '#2E6F73', '#9C6B30'];
export function roasterColour (slug) {
  const r = roasterBySlug(slug)
  if (r?.colour) return r.colour
  let h = 0
  for (const ch of slug) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return FALLBACK[h % FALLBACK.length]
}

/** Every product image goes through our proxy so WebGL can use it (CORS) and it arrives resized. */
export function imgUrl (src, width = 512) {
  if (!src) return ''
  return `${API_BASE}/api/img?w=${width}&u=${encodeURIComponent(src)}`
}

/** Swap in the server's fresher catalogue. The snapshot keeps working if this fails. */
export async function refreshCatalogue () {
  catalogueStatus.value = 'loading'
  try {
    const res = await fetch(`${API_BASE}/api/catalogue`, { headers: { accept: 'application/json' } })
    if (!res.ok) throw new Error(String(res.status))
    const next = await res.json()
    if (Array.isArray(next?.coffees) && next.coffees.length) {
      if (new Date(next.syncedAt) >= new Date(catalogue.value.syncedAt)) catalogue.value = next
    }
    catalogueStatus.value = 'fresh'
  } catch {
    catalogueStatus.value = 'offline'
  }
  return catalogueStatus.value
}

// --- grouping and filters -------------------------------------------------------

export const GROUPINGS = [
  { id: 'roaster', label: 'Roaster' },
  { id: 'flavour', label: 'Flavour' },
  { id: 'brew', label: 'Brew' },
  { id: 'origin', label: 'Origin' },
];
export const grouping = ref(prefs.grouping || 'roaster');
watch(grouping, (g) => { prefs.grouping = g });

const emptyFilters = () => ({ flavours: [], brews: [], kinds: [], origins: [], inStockOnly: false, maxPrice: null });
export const filters = reactive(emptyFilters());
export function clearFilters () { Object.assign(filters, emptyFilters()) }
export const activeFilterCount = computed(() =>
  filters.flavours.length + filters.brews.length + filters.kinds.length + filters.origins.length +
  (filters.inStockOnly ? 1 : 0) + (filters.maxPrice != null ? 1 : 0))

export function matches (c, f = filters) {
  if (f.flavours.length && !f.flavours.includes(c.flavour)) return false
  if (f.brews.length && !f.brews.includes(c.brew)) return false
  if (f.kinds.length && !f.kinds.includes(c.kind)) return false
  if (f.origins.length && !c.origins.some((o) => f.origins.includes(o))) return false
  if (f.inStockOnly && !c.inStock) return false
  if (f.maxPrice != null && (c.fromPrice ?? Infinity) > f.maxPrice) return false
  return true
}
export const matchingSlugs = computed(() => new Set(coffees.value.filter((c) => matches(c)).map((c) => c.slug)))

export const allOrigins = computed(() => {
  const n = {}
  for (const c of coffees.value) for (const o of c.origins) n[o] = (n[o] || 0) + 1
  return Object.entries(n).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }))
})

// The bay a coffee belongs to under a grouping. Blends of several origins go to "Multi-origin".
export function bayKeyOf (c, g = grouping.value) {
  if (g === 'roaster') return c.roasterSlug
  if (g === 'flavour') return c.flavour
  if (g === 'brew') return c.brew
  return c.origins.length === 1 ? c.origins[0] : c.origins.length ? 'Multi-origin' : 'Origin unlisted'
}

// Planogram order within a bay: in stock first, Filter → Omni → Espresso (light to dark), then name.
const brewRank = (b) => Math.max(0, BREWS.indexOf(b))
export function planogramSort (a, b) {
  return (b.inStock - a.inStock) || (brewRank(a.brew) - brewRank(b.brew)) || a.name.localeCompare(b.name)
}

/**
 * The wall. Bays in display order, each with its coffees in planogram order.
 * Special Release coffees are pulled out to the plinth at the start of the wall.
 *   [{ key, title, subtitle, colour, coffees: Coffee[], matchCount }]
 */
export const bays = computed(() => {
  const g = grouping.value
  const special = coffees.value.filter((c) => c.special)
  const rest = coffees.value.filter((c) => !c.special)
  const groups = new Map()
  for (const c of rest) {
    const k = bayKeyOf(c, g)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push(c)
  }
  const meta = (k) => {
    if (g === 'roaster') { const r = roasterBySlug(k); return { title: r?.name || k, colour: roasterColour(k) } }
    if (g === 'flavour') { const f = flavourById[k]; return { title: f?.name || k, colour: f?.colour } }
    if (g === 'brew') return { title: k, subtitle: BREW_BLURB[k], colour: null }
    return { title: k, colour: null }
  }
  let order = [...groups.keys()]
  if (g === 'roaster') order.sort((a, b) => groups.get(b).length - groups.get(a).length)
  else if (g === 'flavour') order.sort((a, b) => FLAVOURS.findIndex((f) => f.id === a) - FLAVOURS.findIndex((f) => f.id === b))
  else if (g === 'brew') order.sort((a, b) => brewRank(a) - brewRank(b))
  else order.sort((a, b) => (a.startsWith('Multi') || a.startsWith('Origin')) - (b.startsWith('Multi') || b.startsWith('Origin')) || groups.get(b).length - groups.get(a).length)
  const out = order.map((key) => {
    const list = groups.get(key).sort(planogramSort)
    return { key, ...meta(key), coffees: list, matchCount: list.filter((c) => matchingSlugs.value.has(c.slug)).length }
  })
  if (special.length) {
    out.unshift({ key: 'special', title: 'Special Release', subtitle: 'Limited drops', special: true, colour: '#F97316', coffees: special, matchCount: special.filter((c) => matchingSlugs.value.has(c.slug)).length })
  }
  return out
})

// --- the bag --------------------------------------------------------------------

const BAG_KEY = 'bag'
const loadBag = () => { try { return Array.isArray(prefs[BAG_KEY]) ? prefs[BAG_KEY] : [] } catch { return [] } }

// /demo shows a bag with three coffees in it, and never touches the real one.
function demoBag () {
  const picks = roasters.value.slice(0, 3).map((r) => coffees.value.find((c) => c.roasterSlug === r.slug && c.inStock)).filter(Boolean)
  return picks.map((c, i) => { const o = c.offers.find((x) => x.inStock) || c.offers[0]; return { id: `demo-${i}`, slug: c.slug, size: o.size, grams: o.grams, price: o.price, qty: 1, addedAt: new Date().toISOString() } })
}

export const bag = ref(isDemo.value ? demoBag() : loadBag())
watch(bag, (b) => { if (!isDemo.value) prefs[BAG_KEY] = b }, { deep: true })

export const bagCount = computed(() => bag.value.reduce((n, i) => n + i.qty, 0))
export const bagTotal = computed(() => bag.value.reduce((n, i) => n + i.price * i.qty, 0))
export const bagLines = computed(() => bag.value.map((i) => ({ ...i, coffee: coffeeBySlug(i.slug) })).filter((l) => l.coffee))

/** Add a coffee in a size. Same coffee + size increments the line. Returns the line. */
export function addToBag (slug, size, qty = 1) {
  const c = coffeeBySlug(slug)
  const o = c?.offers.find((x) => x.size === size)
  if (!c || !o) throw new Error('That size is not available.')
  if (!o.inStock) throw new Error(`${size} is sold out.`)
  const line = bag.value.find((i) => i.slug === slug && i.size === size)
  if (line) { line.qty = Math.min(20, line.qty + qty); return line }
  const item = { id: `${slug}:${size}:${Date.now()}`, slug, size, grams: o.grams, price: o.price, qty, addedAt: new Date().toISOString() }
  bag.value.push(item)
  return item
}
export function setQty (id, qty) {
  const line = bag.value.find((i) => i.id === id)
  if (line) line.qty = Math.max(1, Math.min(20, qty))
}
/** Remove a line; returns a function that puts it back (for undo). */
export function removeFromBag (id) {
  const idx = bag.value.findIndex((i) => i.id === id)
  if (idx < 0) return () => {}
  const [item] = bag.value.splice(idx, 1)
  return () => { bag.value.splice(Math.min(idx, bag.value.length), 0, item) }
}
export function clearBag () {
  const before = bag.value.slice()
  bag.value = []
  return () => { bag.value = before }
}
export const inBag = (slug) => bag.value.some((i) => i.slug === slug)

// --- settings -------------------------------------------------------------------

export const settings = reactive({
  viewMode: prefs.viewMode || 'auto',   // auto | wall | list  (auto → list under a screen reader or without WebGL)
  motion: prefs.motion || 'system',     // system | full | reduced
  quality: prefs.quality || 'auto',     // auto | high | saver
  onboarded: !!prefs.onboarded,
})
watch(settings, (s) => { for (const k of Object.keys(s)) prefs[k] = s[k] }, { deep: true })

export const reducedMotion = computed(() =>
  settings.motion === 'reduced' || (settings.motion === 'system' && prefersReducedMotion()))

export const webglOK = (() => {
  try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')) } catch { return false }
})()

/** 'wall' or 'list' — what the Shelf screen actually shows. */
export const effectiveView = computed(() => {
  if (!webglOK) return 'list'
  if (settings.viewMode === 'wall' || settings.viewMode === 'list') return settings.viewMode
  return 'wall'
})

/** The picked coffee on the Shelf (Label sheet open). Shared so the tab bar can react. */
export const picked = ref(null)
